import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap";

export default class extends Controller {
  static targets = [
    "selectedTagsContainer",
    "createBundleForm",
    "createToggleButton",
    "searchInput",
    "searchResultsContainer",
    "recentSection",
    "bundleItem",
    "newBundleName",
    "visibilityPublic",
    "createBundleButton",
    "recordCheckbox",
    "recordsCount",
    "researchNotes",
    "saveButton",
    "popover",
    "createNameError",
  ];

  static outlets = ["datagrid--status-feedback"];

  static values = {
    searchUrl: String,
    createUrl: String,
    saveUrl: String,
    records: String,
    translations: Object,
    showFlash: { type: Boolean, default: false },
    survivorMode: { type: Boolean, default: false },
  };

  initialize() {
    this.selectedBundleIds = new Set();
    this.searchTimeout = null;
    this.bundleDataMap = new Map();
    this.updateSaveButton();
  }

  t(key) {
    return this.translationsValue[key] || key;
  }

  toggleBundle(event) {
    const item = event.currentTarget;
    const bundleId = item.dataset.bundleId;

    if (this.selectedBundleIds.has(bundleId)) {
      this.selectedBundleIds.delete(bundleId);
      item.classList.remove("selected");
    } else {
      this.selectedBundleIds.add(bundleId);
      item.classList.add("selected");
      this.storeBundleData(item);
    }
    this.renderTags();
    this.updateSaveButton();
  }

  deselectBundle(id) {
    this.selectedBundleIds.delete(id);
    this.bundleItemTargets.forEach((item) => {
      if (item.dataset.bundleId === id) {
        item.classList.remove("selected");
      }
    });
    this.renderTags();
    this.updateSaveButton();
  }

  renderTags() {
    const container = this.selectedTagsContainerTarget;
    container.innerHTML = "";

    if (this.selectedBundleIds.size === 0) {
      const empty = document.createElement("div");
      empty.className = "adding-to-empty py-1";
      empty.textContent = this.t("no_bundles_selected");
      container.appendChild(empty);
      return;
    }

    this.selectedBundleIds.forEach((id) => {
      const data = this.bundleDataMap.get(id);
      if (!data) return;

      const tag = document.createElement("div");
      tag.className = "selected-bundle-tag px-2 mb-1";

      const nameSpan = document.createElement("span");
      nameSpan.textContent = data.name;
      tag.appendChild(nameSpan);

      const removeBtn = document.createElement("button");
      removeBtn.className = "p-0 ps-2";
      removeBtn.innerHTML = "×";
      removeBtn.title = this.t("remove");
      removeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.deselectBundle(id);
      });
      tag.appendChild(removeBtn);

      this.setupPopoverForElement(tag, id);
      container.appendChild(tag);
    });
  }

  storeBundleData(element) {
    const id = element.dataset.bundleId;
    if (!this.bundleDataMap.has(id)) {
      this.bundleDataMap.set(id, {
        name: element.dataset.bundleName || "",
        visibility: element.dataset.bundleVisibility || "private",
        access: element.dataset.bundleAccess || "",
        created: element.dataset.bundleCreated || "",
        notes: element.dataset.bundleNotes || "",
        approved: element.dataset.bundleApproved === "true",
      });
    }
  }

  scanBundleData() {
    this.bundleItemTargets.forEach((el) => {
      this.storeBundleData(el);
    });
  }

  setupPopoverForElement(element, bundleId) {
    let hoverTimer;
    element.addEventListener("mouseenter", () => {
      hoverTimer = setTimeout(() => this.showPopover(bundleId, element), 300);
    });
    element.addEventListener("mouseleave", () => {
      clearTimeout(hoverTimer);
      this.hidePopover();
    });
  }

  showPopover(bundleId, anchorElement) {
    if (!this.hasPopoverTarget) return;
    const data = this.bundleDataMap.get(bundleId);
    if (!data) return;

    const popover = this.popoverTarget;
    const isPublic = data.visibility === "public";

    const badgeClass = isPublic ? "public" : "private";
    const badgeLabel = isPublic ? this.t("public") : this.t("private");

    const hasAccess = isPublic || (data.access && data.access !== this.t("no_external_access"));
    const accessHtml = hasAccess
      ? `<span class="popover-val">${data.access}</span>`
      : `<span class="popover-val muted">${data.access}</span>`;

    let notesHtml = "";
    if (data.notes) {
      notesHtml = `
        <div class="popover-divider"></div>
        <div class="popover-field">
          <span class="popover-key">${this.t("research_notes")}</span>
          <span class="popover-val popover-notes">${data.notes}</span>
        </div>`;
    }

    popover.innerHTML = `
      <div class="popover-row gap-2">
        <div class="popover-field">
          <span class="popover-key">${this.t("visibility")}</span>
          <span class="popover-badge py-0 px-2 ${badgeClass}">${badgeLabel}</span>
        </div>
        <div class="popover-divider"></div>
        <div class="popover-field">
          <span class="popover-key">${this.t("access")}</span>
          ${accessHtml}
        </div>
        <div class="popover-divider"></div>
        <div class="popover-field">
          <span class="popover-key">${this.t("created")}</span>
          <span class="popover-val">${data.created}</span>
        </div>
        ${notesHtml}
      </div>
    `;

    const rect = anchorElement.getBoundingClientRect();
    popover.style.top = `${rect.top + rect.height / 2 - 16}px`;
    popover.style.left = `${rect.right + 10}px`;
    popover.classList.add("visible");
  }

  hidePopover() {
    if (this.hasPopoverTarget) {
      this.popoverTarget.classList.remove("visible");
    }
  }

  bundleItemTargetConnected(el) {
    this.storeBundleData(el);
    this.setupPopoverForElement(el, el.dataset.bundleId);
    if (this.selectedBundleIds.has(el.dataset.bundleId)) {
      el.classList.add("selected");
    }
  }

  searchBundles() {
    if (this.survivorModeValue) return;
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      const query = this.searchInputTarget.value.trim();
      const showingResults = query.length > 0;

      const url = `${this.searchUrlValue}?query=${encodeURIComponent(query)}`;

      fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.head.querySelector("meta[name='csrf-token']")?.content,
        },
      })
        .then((response) => response.text())
        .then((html) => {
          Turbo.renderStreamMessage(html);
          setTimeout(() => {
            this.scanBundleData();
            this.reapplySelections();
            if (this.hasRecentSectionTarget) {
              this.recentSectionTarget.classList.toggle("d-none", showingResults);
            }
            if (this.hasSearchResultsContainerTarget) {
              this.searchResultsContainerTarget.classList.toggle("visible", showingResults);
            }
          }, 50);
        })
        .catch((error) => console.error("Search error:", error));
    }, 300);
  }

  reapplySelections() {
    this.bundleItemTargets.forEach((item) => {
      if (this.selectedBundleIds.has(item.dataset.bundleId)) {
        item.classList.add("selected");
      }
    });
  }

  showCreateBundleForm() {
    if (this.hasCreateBundleFormTarget) {
      this.createBundleFormTarget.classList.toggle("open");
    }
    if (this.hasCreateToggleButtonTarget) {
      this.createToggleButtonTarget.classList.toggle("open");
    }
    if (this.hasCreateBundleFormTarget && this.createBundleFormTarget.classList.contains("open")) {
      this.newBundleNameTarget.focus();
    }
  }

  hideCreateBundleForm() {
    if (this.hasCreateBundleFormTarget) {
      this.createBundleFormTarget.classList.remove("open");
    }
    if (this.hasCreateToggleButtonTarget) {
      this.createToggleButtonTarget.classList.remove("open");
    }
    this.newBundleNameTarget.value = "";
    this.createBundleButtonTarget.disabled = true;
    this.clearCreateNameError();
  }

  showCreateNameError(message) {
    if (!this.hasCreateNameErrorTarget) return;
    this.newBundleNameTarget.classList.add("is-invalid");
    this.createNameErrorTarget.textContent = message;
    this.createNameErrorTarget.style.display = "block";
  }

  clearCreateNameError() {
    if (!this.hasCreateNameErrorTarget) return;
    this.newBundleNameTarget.classList.remove("is-invalid");
    this.createNameErrorTarget.textContent = "";
    this.createNameErrorTarget.style.display = "none";
  }

  updateCreateBundleButton() {
    this.createBundleButtonTarget.disabled = this.newBundleNameTarget.value.trim().length === 0;
  }

  createBundle(event) {
    event.preventDefault();

    const name = this.newBundleNameTarget.value.trim();
    if (!name) return;

    const bundleParams = { nu_name: name };
    if (this.hasVisibilityPublicTarget) {
      bundleParams.nu_is_public = this.visibilityPublicTarget.checked;
    }
    const csrfToken = document.head.querySelector("meta[name='csrf-token']")?.content;

    fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ nu_bundle: bundleParams }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data.id) {
          this.clearCreateNameError();
          const bundleId = String(data.id);
          const isPublic = bundleParams.nu_is_public || false;
          const bundleData = {
            name: data.name,
            visibility: isPublic ? "public" : "private",
            access: isPublic ? this.t("everyone") : this.t("no_external_access"),
            created: new Date().toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            }),
            notes: "",
          };
          this.bundleDataMap.set(bundleId, bundleData);
          this.selectedBundleIds.add(bundleId);
          this.appendRecentBundleItem(bundleId, bundleData);
          this.hideCreateBundleForm();
          this.renderTags();
          this.updateSaveButton();
        } else if (data.errors) {
          this.showCreateNameError(data.errors.join(", "));
        }
      })
      .catch((error) => console.error("Create error:", error));
  }

  appendRecentBundleItem(bundleId, bundleData) {
    const list = this.element.querySelector("#recent-bundle-list");
    const template = this.bundleItemTargets[0];
    if (!list || !template) return;

    const item = template.cloneNode(true);
    item.classList.add("selected");
    item.dataset.bundleId = bundleId;
    item.dataset.bundleName = bundleData.name;
    item.dataset.bundleVisibility = bundleData.visibility;
    item.dataset.bundleAccess = bundleData.access;
    item.dataset.bundleCreated = bundleData.created;
    item.dataset.bundleNotes = bundleData.notes;
    item.querySelector(".bundle-name-text").textContent = bundleData.name;

    list.appendChild(item);
  }

  saveToBundle(event) {
    event.preventDefault();

    if (this.selectedBundleIds.size === 0) return;

    const records = this.getSelectedRecords();
    if (records.length === 0) return;

    if (this.anySelectedBundleApproved() && !window.confirm(this.t("revert_selected_to_draft_confirm"))) {
      return;
    }

    const payload = {
      bundle_ids: Array.from(this.selectedBundleIds),
      records: records,
    };
    if (this.hasResearchNotesTarget) {
      payload.research_notes = this.researchNotesTarget.value.trim();
    }

    fetch(this.saveUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.head.querySelector("meta[name='csrf-token']")?.content,
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (response.ok) {
          const savedMessage = this.buildSavedMessage(records.length, this.selectedBundleIds.size);
          this.showStatusFeedback(savedMessage);
          if (this.showFlashValue) {
            this.showFlashMessage(savedMessage);
          }
          this.closeModal();
        }
      })
      .catch((error) => console.error("Save error:", error));
  }

  clickRecord(event) {
    if (this.survivorModeValue) return;
    if (event.target.classList.contains("record-checkbox")) return;
    const card = event.currentTarget;
    const checkbox = card.querySelector(".record-checkbox");
    if (checkbox) {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  toggleRecord(event) {
    const checkbox = event.currentTarget;
    const card = checkbox.closest(".bundle-record-card");
    if (card) {
      card.classList.toggle("excluded", !checkbox.checked);
    }
    this.updateRecordsCount();
    this.updateSaveButton();
  }

  updateRecordsCount() {
    if (!this.hasRecordsCountTarget) return;
    const checkedCount = this.recordCheckboxTargets.filter((cb) => cb.checked).length;
    this.recordsCountTarget.textContent = `(${checkedCount})`;
  }

  updateSaveButton() {
    const hasRecords = this.survivorModeValue
      ? this.getSelectedRecords().length > 0
      : this.recordCheckboxTargets.some((cb) => cb.checked);
    const bundleCount = this.selectedBundleIds.size;

    this.saveButtonTarget.disabled = !hasRecords || bundleCount === 0;

    if (bundleCount <= 1) {
      this.saveButtonTarget.textContent = this.t("save_to_bundle");
    } else {
      this.saveButtonTarget.textContent = this.t("save_to_bundles").replace("%{count}", bundleCount);
    }
  }

  anySelectedBundleApproved() {
    for (const id of this.selectedBundleIds) {
      if (this.bundleDataMap.get(id)?.approved) return true;
    }
    return false;
  }

  getSelectedRecords() {
    if (this.survivorModeValue) {
      return JSON.parse(this.recordsValue);
    }

    return this.recordCheckboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => ({
        type: cb.dataset.recordType,
        id: cb.dataset.recordId,
      }));
  }

  pluralize(count, forms) {
    const rule = new Intl.PluralRules("en").select(count);
    return (forms[rule] || forms["other"]).replace("%{count}", count);
  }

  buildSavedMessage(recordCount, bundleCount) {
    const recordsText = this.pluralize(recordCount, this.translationsValue["saved_records"]);
    const bundlesText = this.pluralize(bundleCount, this.translationsValue["saved_bundles"]);
    return this.t("saved_to_bundle").replace("%{records}", recordsText).replace("%{bundles}", bundlesText);
  }

  showStatusFeedback(message) {
    if (!this.hasDatagridStatusFeedbackOutlet) return;
    this.datagridStatusFeedbackOutlet.showAndHideWithoutSpinner(message);
  }

  showFlashMessage(message) {
    let flash = document.getElementById("flash");
    if (!flash) {
      flash = document.createElement("div");
      flash.id = "flash";
      const container = document.querySelector(".app-layout-body");
      if (container) {
        container.prepend(flash);
      } else {
        return;
      }
    }

    const alert = document.createElement("div");
    alert.className = "alert alert-info alert-dismissible fade show";
    alert.setAttribute("role", "alert");
    alert.innerHTML = `${message}<button class="btn-close" type="button" data-bs-dismiss="alert" aria-label="Close"></button>`;
    flash.innerHTML = "";
    flash.appendChild(alert);
  }

  closeModal() {
    const modal = this.element.closest(".modal");
    if (modal) {
      const modalInstance = Modal.getInstance(modal);
      if (modalInstance) {
        modalInstance.hide();
      }
    }
  }
}
